import { useState, useEffect, useRef } from 'react';
import { X, Volume2, VolumeX, RotateCcw, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY  = 'seva_video_seen';
const CLOSE_KEY = 'seva_video_closed';

export default function VideoWidget() {
  const [visible, setVisible]       = useState(false);
  const [muted, setMuted]           = useState(true);
  const [ended, setEnded]           = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [videoUrl, setVideoUrl]     = useState('');

  // Determined once at mount — true = absolute first ever visit
  const isFirstTime = useRef(!localStorage.getItem(SEEN_KEY));
  const videoRef    = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem(CLOSE_KEY)) return;
    axios.get(`${API}/marketing/video`)
      .then(r => {
        if (r.data.video_url) {
          setVideoUrl(r.data.video_url);
          setVisible(true);
        }
      })
      .catch(() => {});
  }, []);

  /* ── When the video element is ready & it's a first-time visit → autoplay ── */
  const handleCanPlay = () => {
    if (!isFirstTime.current || hasStarted) return;
    localStorage.setItem(SEEN_KEY, '1');         // mark seen for future visits
    videoRef.current.play()
      .then(() => setHasStarted(true))
      .catch(() => { /* browser blocked autoplay – play overlay stays */ });
  };

  const handleUserPlay = () => {
    videoRef.current.play()
      .then(() => setHasStarted(true))
      .catch(() => {});
  };

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem(CLOSE_KEY, '1');
    if (videoRef.current) videoRef.current.pause();
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setEnded(false);
      setHasStarted(true);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) videoRef.current.muted = !muted;
    setMuted(m => !m);
  };

  if (!videoUrl) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.92 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 right-4 sm:right-6 z-[9999] w-[160px] sm:w-[190px] overflow-hidden rounded-xl shadow-2xl shadow-black/60"
          data-testid="video-widget"
        >
          <div className="relative">
            {/* Video — 9:16 portrait ratio */}
            <video
              ref={videoRef}
              src={videoUrl}
              muted={muted}
              playsInline
              onCanPlay={handleCanPlay}
              onEnded={() => setEnded(true)}
              className="w-full block"
              style={{ aspectRatio: '9 / 16', objectFit: 'cover' }}
              data-testid="video-player"
            />

            {/* ── Play overlay — shown on return visits before user taps play ── */}
            {!hasStarted && !ended && (
              <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3">
                <button
                  onClick={handleUserPlay}
                  data-testid="video-play-btn"
                  aria-label="Play video"
                  className="w-14 h-14 rounded-full bg-white/20 border-2 border-white/70 flex items-center justify-center text-white hover:bg-white/35 hover:border-white transition-all duration-200 group"
                >
                  <Play size={22} className="translate-x-0.5 group-hover:scale-110 transition-transform" />
                </button>
                <span className="text-white/70 text-[10px] font-semibold uppercase tracking-widest">Watch Video</span>
                {/* Close still accessible on the overlay */}
                <button
                  onClick={handleClose}
                  data-testid="video-close-btn-overlay"
                  aria-label="Close"
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/55 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {/* ── Top bar: mute + close (visible once playing) ── */}
            {hasStarted && !ended && (
              <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                <button
                  onClick={toggleMute}
                  data-testid="video-mute-btn"
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                >
                  {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                </button>
                <button
                  onClick={handleClose}
                  data-testid="video-close-btn"
                  aria-label="Close"
                  className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {/* ── Unmute hint — bottom centre, only while muted & playing ── */}
            {muted && hasStarted && !ended && (
              <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                <button
                  onClick={toggleMute}
                  data-testid="video-unmute-hint"
                  className="bg-black/55 backdrop-blur-sm text-white text-[10px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1 hover:bg-black/75 transition-colors"
                >
                  <VolumeX size={9} /> Tap to unmute
                </button>
              </div>
            )}

            {/* ── Ended overlay — replay button ── */}
            {ended && (
              <div className="absolute inset-0 bg-black/65 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2">
                <button
                  onClick={handleReplay}
                  data-testid="video-replay-btn"
                  className="flex flex-col items-center gap-2 text-white group"
                >
                  <div className="w-12 h-12 rounded-full border-2 border-white/60 group-hover:border-white flex items-center justify-center group-hover:bg-white/10 transition-all duration-200">
                    <RotateCcw size={20} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/75">Replay</span>
                </button>
                <button onClick={handleClose} className="mt-1 text-white/40 text-[9px] uppercase tracking-wider hover:text-white/70 transition-colors">
                  Close
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
